<script setup lang="ts">
import Button, { type ButtonPassThroughOptions } from 'primevue/button'
import { ref } from 'vue'
import { ptViewMerge } from './utils'

// Don't define props - let everything pass through via $attrs
// This allows icon, label, severity, etc. to reach the PrimeVue Button
defineOptions({ inheritAttrs: false })

const theme = ref<ButtonPassThroughOptions>({
  root: `inline-flex cursor-pointer select-none items-center justify-center overflow-hidden relative
        px-3 py-2 gap-2 rounded-md disabled:pointer-events-none disabled:opacity-60 transition-colors duration-200
        bg-[var(--btn-primary-bg)] enabled:hover:bg-[var(--btn-primary-bg-hover)] enabled:active:bg-[var(--btn-primary-bg-active)]
        border border-[var(--btn-primary-border)] enabled:hover:border-[var(--btn-primary-border-hover)] enabled:active:border-[var(--btn-primary-border-active)]
        text-[var(--btn-primary-text)]
        focus-visible:outline focus-visible:outline-1 focus-visible:outline-offset-2
        focus-visible:outline-[var(--btn-primary-border)]
        p-vertical:flex-col p-fluid:w-full p-fluid:p-icon-only:w-10
        p-icon-only:w-10 p-icon-only:px-0 p-icon-only:gap-0
        p-icon-only:p-rounded:rounded-full p-icon-only:p-rounded:h-10
        p-small:text-sm p-small:px-[0.625rem] p-small:py-[0.375rem]
        p-large:text-[1.125rem] p-large:px-[0.875rem] p-large:py-[0.625rem]
        p-raised:shadow-sm p-rounded:rounded-[2rem]
        p-outlined:bg-transparent enabled:hover:p-outlined:bg-[var(--btn-primary-outlined-bg-hover)] enabled:active:p-outlined:bg-[var(--btn-primary-outlined-bg-active)]
        p-outlined:border-[var(--btn-primary-border)] enabled:hover:p-outlined:border-[var(--btn-primary-border)] enabled:active:p-outlined:border-[var(--btn-primary-border)]
        p-outlined:text-[var(--btn-primary-outlined-text)] enabled:hover:p-outlined:text-[var(--btn-primary-outlined-text)] enabled:active:p-outlined:text-[var(--btn-primary-outlined-text)]
    `,
  /* NOTE: p-text (text buttons) styling is handled globally in tokens.css
       using [data-p*="text"] selector for reliable Light/Dark mode support */
  loadingIcon: `animate-spin`,
  icon: `p-right:order-1 p-bottom:order-2 text-current`,
  label: `font-medium p-icon-only:invisible p-icon-only:w-0
        p-small:text-sm p-large:text-[1.125rem]`,
  pcBadge: {
    root: `min-w-4 h-4 leading-4 rounded-full text-xs font-bold bg-[var(--btn-primary-text)] text-[var(--btn-primary-bg)]`,
  },
})
</script>

<template>
  <Button
    v-bind="$attrs"
    unstyled
    :pt="theme"
    :pt-options="{
      mergeProps: ptViewMerge,
    }"
  >
    <template
      v-for="(_, slotName) in $slots"
      #[slotName]="slotProps"
    >
      <slot
        :name="slotName"
        v-bind="slotProps ?? {}"
      />
    </template>
  </Button>
</template>
